import { graphql } from '@lib/graphql'
import { execute } from '@lib/graphql/execute'
import {
  ShopTheLookProductFragment,
  ShopTheLookProductsQueryVariables,
} from '@lib/graphql/graphql'
import { QueryConfig } from '@lib/react-query'
import { queryOptions, useQuery } from '@tanstack/react-query'

const shopTheLookProductsQueryDocument = graphql(`
  query ShopTheLookProducts(
    $query: String
    $channelId: ID!
    $currency: String
    $after: String
  ) {
    products(first: 10, query: $query, after: $after)
      @context(channelId: $channelId, currency: $currency) {
      edges {
        node {
          ...ShopTheLookProduct
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }

  fragment ShopTheLookProduct on Product {
    id
    slug
    name
    description
    vendor

    variants(first: 50) {
      edges {
        node {
          ...ShopTheLookProductVariant
        }
      }
    }
  }
  fragment ShopTheLookProductVariant on ProductVariant {
    id
    sku
    name
    media {
      edges {
        node {
          src
          fileName
        }
      }
    }
    selectedAttributes {
      name
      value
    }
    price {
      value {
        centAmount
        currencyCode
        fractionDigits
      }
    }
  }
`)

const fetchShopTheLookProducts = async (
  channelId: string,
  currency: string,
  query: string | null,
  after: string | null = null,
  collectedTiles: ShopTheLookProductFragment[] = [],
): Promise<ShopTheLookProductFragment[]> => {
  const response = await execute(shopTheLookProductsQueryDocument, {
    channelId,
    after,
    currency,
    query,
  })

  if (!response.products) {
    return collectedTiles
  }
  const { edges, pageInfo } = response.products

  const products = await Promise.all(
    edges.map(async (edge) => {
      const product = edge.node

      return product
    }),
  )

  collectedTiles.push(...products)

  if (pageInfo.hasNextPage) {
    return fetchShopTheLookProducts(
      channelId,
      currency,
      query,
      pageInfo.endCursor,
      collectedTiles,
    )
  }

  return collectedTiles
}

export const getShopTheLookProducts = async ({
  channelId,
  currency,
  query,
}: ShopTheLookProductsQueryVariables): Promise<
  ShopTheLookProductFragment[]
> => {
  return fetchShopTheLookProducts(channelId, currency, query, null)
}

const getShopTheLookVariantQueryOptions = (
  variables: ShopTheLookProductsQueryVariables,
) => {
  return queryOptions({
    queryKey: ['shop-the-look-products', variables],
    queryFn: () => getShopTheLookProducts(variables),
  })
}

export const useShopThelookVariants = ({
  variables,
  queryConfig,
}: {
  variables?: ShopTheLookProductsQueryVariables
  queryConfig?: QueryConfig<typeof getShopTheLookVariantQueryOptions>
} = {}) => {
  return useQuery({
    ...getShopTheLookVariantQueryOptions(variables),
    ...queryConfig,
  })
}
